/**
 * 表セクションのノードツリーを表示用ビューモデルに parse するモジュール。
 *
 * 表セクションはノードツリー上では「表セクション本体 → 行セクション → セル質問」
 * という入れ子構造だが、UI では「列 × 行のグリッド」として表示する。
 *
 * ## 変換のルール
 *
 * - 列 = テンプレート行を平坦化した質問。列の直後に、その列の条件分岐配下の分岐質問を
 *   深さ優先で並べる。分岐質問もさらに条件分岐を持てるので、同じ規則で降りていく。
 *   条件分岐ノード自体は列にしない（条件であって回答対象ではないため）。
 * - 表示する行 = 固定行と記録中に追加された行。テンプレート行自体は表示しない。
 *   並び順は表セクションの nodes 配列順。
 * - セル = 各行を列と同じ規則で平坦化し、列と同じ index で対応付ける。
 *   分岐質問のセルは、祖先の条件分岐をすべて満たさなければ非活性（active = false）。
 */
import {
  ReportNodeSchema,
  ResponseTypeEnum,
  SectionTypeEnum,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import getCalcNumberLogicType from 'src/exShared/hooks/reports/getCalcNumberLogicType'
import { ReportNodesDict } from 'src/exShared/types/types'
import { isDefined } from 'src/util/isDefined'
import { isNullish } from 'src/util/isNullish'

/** `sectionType = TABLE` の表セクションノードかどうか。 */
export const isTableSectionNode = (node: ReportNodeSchema): boolean =>
  node.type === TemplateNodeTypeEnum.Section &&
  node.section?.sectionType === SectionTypeEnum.Table

/** `sectionType = TABLE_ROW` の行セクションノードかどうか。 */
export const isTableRowSectionNode = (node: ReportNodeSchema): boolean =>
  node.type === TemplateNodeTypeEnum.Section &&
  node.section?.sectionType === SectionTypeEnum.TableRow

const isQuestionNode = (node: ReportNodeSchema): boolean =>
  node.type === TemplateNodeTypeEnum.Question

const isLogicNode = (node: ReportNodeSchema): boolean =>
  node.type === TemplateNodeTypeEnum.Logic && isDefined(node.logic)

/**
 * テンプレート行かどうか。表セクションは必ず1つ持つ。
 * 記録中に追加された行も isRepeat = 1 だが、複製元を指す originalNodeUuid を持つ。
 * テンプレート行はそれを持たないことで区別する。
 */
const isTemplateRowNode = (node: ReportNodeSchema): boolean =>
  isTableRowSectionNode(node) &&
  node.section?.isRepeat === 1 &&
  !node.section?.originalNodeUuid

/** 表の1セル。条件を満たさない分岐質問のセルは非活性（UI では「ー」）。 */
export type TableSectionCell = {
  questionNode: ReportNodeSchema
  active: boolean
}

export type TableSectionViewRow = {
  /** 行セクションの node */
  rowNode: ReportNodeSchema
  /**
   * 表全体での行番号（1 始まり）。逸脱項目ビューで行を絞り込んでも
   * 全体表示と同じ番号を保てるよう、行自身が持つ。
   */
  rowNumber: number
  heading: string
  /** 列と同じ index で対応するセル */
  cells: TableSectionCell[]
}

export type TableSectionView = {
  /** テンプレート行を平坦化した質問 node（列 + 分岐質問） */
  columnNodes: ReportNodeSchema[]
  /** 行見出し列の列名。行見出し列がない表では undefined */
  headingLabel: string | undefined
  rows: TableSectionViewRow[]
}

/** 活性判定に使う条件分岐1つと、その条件分岐を持つ質問。 */
type BranchLogic = {
  logicNode: ReportNodeSchema
  parentQuestionNode: ReportNodeSchema
}

/** 平坦化された質問1つ。 */
type FlattenedQuestion = {
  questionNode: ReportNodeSchema
  /**
   * 根から順に並べた、この質問にたどり着くまでの条件分岐。
   * 列そのものは空配列で、要素数がこの質問の深さになる。
   */
  branchLogics: BranchLogic[]
}

/**
 * 質問直下の条件分岐をたどり、その配下の分岐質問を深さ優先で取り出す。
 * 分岐質問もさらに条件分岐を持てるので、同じ規則で降りていく。
 */
const flattenBranchQuestions = (
  parentQuestionNode: ReportNodeSchema,
  ancestorLogics: BranchLogic[],
  allNodes: ReportNodesDict,
): FlattenedQuestion[] =>
  parentQuestionNode.nodes
    .map(nodeId => allNodes[nodeId])
    .filter(isDefined)
    .filter(isLogicNode)
    .flatMap(logicNode => {
      const branchLogics = [
        ...ancestorLogics,
        { logicNode, parentQuestionNode },
      ]
      return logicNode.nodes
        .map(nodeId => allNodes[nodeId])
        .filter(isDefined)
        .filter(isQuestionNode)
        .flatMap(questionNode => [
          { questionNode, branchLogics },
          ...flattenBranchQuestions(questionNode, branchLogics, allNodes),
        ])
    })

/**
 * 行セクション配下を nodes 配列順で平坦化する。
 * 各列を先頭に、その列の分岐質問を続けて並べる（条件分岐ノード自体は含めない）。
 */
const flattenRowQuestions = (
  rowSectionNode: ReportNodeSchema,
  allNodes: ReportNodesDict,
): FlattenedQuestion[] =>
  rowSectionNode.nodes
    .map(nodeId => allNodes[nodeId])
    .filter(isDefined)
    .filter(isQuestionNode)
    .flatMap(columnNode => [
      { questionNode: columnNode, branchLogics: [] },
      ...flattenBranchQuestions(columnNode, [], allNodes),
    ])

/**
 * 条件分岐1つを満たすか（その条件分岐を持つ質問の回答が条件に合致するか）。
 * 未回答・不一致なら満たさない。
 *
 * 判定規則は通常質問の条件分岐表示（getActiveQuestions）と同じだが、あちらは
 * 「表示する質問の一覧」を返す API でセル単位の活性判定には使いにくいため、ここに小さく持つ。
 */
const isBranchLogicSatisfied = (branch: BranchLogic): boolean => {
  const baseQuestion = branch.parentQuestionNode.question
  switch (baseQuestion?.responseType) {
    case ResponseTypeEnum.MULTIPLE_CHOICE: {
      const response = baseQuestion.responseAnswer?.multipleChoice?.response
      if (response === undefined) return false
      return (branch.logicNode.logic?.responseMultipleChoices ?? []).some(
        choice => choice.response === response,
      )
    }
    case ResponseTypeEnum.NUMBER: {
      const numberValue = baseQuestion.responseAnswer?.numberValue?.numberValue
      if (isNullish(numberValue)) return false
      const numberLogicType = getCalcNumberLogicType(
        numberValue,
        baseQuestion.responseNumbers?.[0],
      )
      return (branch.logicNode.logic?.numberConditions ?? []).some(
        condition => condition.logicType === numberLogicType,
      )
    }
    default:
      return false
  }
}

const toCell = ({
  questionNode,
  branchLogics,
}: FlattenedQuestion): TableSectionCell => ({
  questionNode,
  // 列は常に活性（branchLogics が空）。分岐質問は祖先の条件をすべて満たすときだけ活性。
  active: branchLogics.every(isBranchLogicSatisfied),
})

/**
 * 表セクションノードを表示用ビューモデルに parse する。
 * データ仕様を満たさない（テンプレート行がない・行のセル数が列数と一致しない）場合は
 * 表全体を不正とみなし undefined を返す。
 *
 * 表のデータ構造の正しさはバックエンドで保証されていることを期待する。
 * そのため、表示に関係する最小限のチェックだけを行い、厳密な構造の検証は行わない。
 */
export const parseTableSection = (
  tableSectionNode: ReportNodeSchema,
  allNodes: ReportNodesDict,
): TableSectionView | undefined => {
  const rowSectionNodes = tableSectionNode.nodes
    .map(nodeId => allNodes[nodeId])
    .filter(isDefined)
    .filter(isTableRowSectionNode)

  const masterRowNode = rowSectionNodes.find(isTemplateRowNode)
  if (masterRowNode === undefined) return undefined

  const columns = flattenRowQuestions(masterRowNode, allNodes)

  const rows = rowSectionNodes
    .filter(node => !isTemplateRowNode(node))
    .map((rowNode, index) => ({
      rowNode,
      // 表全体での通し番号。逸脱フィルタで行を絞り込んでもこの番号は変わらない
      rowNumber: index + 1,
      heading: rowNode.section?.name ?? '',
      cells: flattenRowQuestions(rowNode, allNodes).map(toCell),
    }))

  // 1行でも列数とセル数が一致しなければデータ仕様違反として表全体を不正扱いにする。
  if (rows.some(row => row.cells.length !== columns.length)) {
    return undefined
  }

  return {
    columnNodes: columns.map(column => column.questionNode),
    headingLabel: masterRowNode.section?.name || undefined,
    rows,
  }
}
